import { memo, useCallback, useEffect, useState, useRef } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import useMNPAudio from "../../../hooks/useMNPAudio"
import useSoundAlert, { SoundType } from "../../../hooks/useSoundAlert"

import {
  privateRoomDataAtom,
  privateRoomSelector,
  startPrivateRoomAtom,
  privateRoomTabTypeAtom
} from "../../../recoil/atoms/privateRoom"
import { localUserCallbackSelector } from "../../../recoil/atoms/users"
import { exitFullScreen } from "../../../util"

import CustomOverlayModal from "../../common/CustomOverlayModal"
import usePrivateRoom from "./usePrivateRoom"

const _PrivateRoomHandler = () => {
  const [showModal, setShowModal] = useState(false)
  const startPrivateRoom = useRecoilValue(startPrivateRoomAtom)
  const privateRoomTabType = useRecoilValue(privateRoomTabTypeAtom)
  const privateRoomData = useRecoilValue(privateRoomDataAtom)
  const setPrivateRoom = useSetRecoilState(privateRoomSelector)
  const localUserCallbackProps = useRecoilValue(localUserCallbackSelector)

  const timerIdRef = useRef(null)
  const playSound = useSoundAlert()
  const { openPrivateRoom } = usePrivateRoom()
  const { fadeOutMNPAudio, fadeInMNPAudio } = useMNPAudio()

  const handleClose = useCallback(() => {
    setPrivateRoom({ action: "RESET" }) // reset private room
    clearInterval(timerIdRef.current) // stop play notification sound
    setShowModal(false) // hide modal
  }, [setPrivateRoom])

  const handleIgnore = useCallback(() => {
    handleClose()
    fadeInMNPAudio()
  }, [handleClose, fadeInMNPAudio])

  const handleOpenPrivateRoom = async () => {
    openPrivateRoom(privateRoomData, privateRoomTabType)
    handleClose()
  }

  const playSoundTillUserResponse = () => {
    setTimeout(() => {
      playSound(SoundType.BLEEP)
    }, 1000)
    // play bleep sound every 10 secs
    timerIdRef.current = setInterval(() => {
      if (document.hidden) {
        playSound(SoundType.BLEEP)
      }
    }, 10000) // 10 sec
  }

  useEffect(() => {
    const initialize = async () => {
      if (startPrivateRoom) {
        const localUser = await localUserCallbackProps.handleReturnData()
        if (privateRoomData.createUserKey === localUser.id) {
          // Private room creator user
          handleOpenPrivateRoom()
        } else {
          setShowModal(true) // open private room overlay
          await exitFullScreen()
          await fadeOutMNPAudio()
          playSoundTillUserResponse()
        }
      }
    }
    initialize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPrivateRoom])

  if (showModal) {
    return (
      <CustomOverlayModal
        title="You have been invited to a private room."
        onCancel={handleIgnore}
        onConfirm={handleOpenPrivateRoom}
      />
    )
  }
  return null
}

const PrivateRoomHandler = memo(_PrivateRoomHandler)

export default PrivateRoomHandler
